<script setup lang="ts">
import { computed } from 'vue'
import { CheckCircle2, CircleDashed, Download, FileCheck2, Printer, ShieldCheck, TriangleAlert, XCircle } from 'lucide-vue-next'
import type { DataValidationResult } from '@clinic-crf/contract'
import { integrityLabel, type IntegrityState } from '../helpers'

const props = defineProps<{
  validation: DataValidationResult
  visited: ReadonlySet<string>
  hash: string
  integrity: IntegrityState
}>()

defineEmits<{
  exportReport: []
  printReport: []
}>()

const visibleFields = computed(() => props.validation.fields.filter((entry) => entry.visible))
const visitedVisible = computed(() => visibleFields.value.filter((entry) => props.visited.has(entry.field.path)))
const unvisited = computed(() => visibleFields.value.filter((entry) => !props.visited.has(entry.field.path)))
const activeRules = computed(() => props.validation.rules.filter((entry) => entry.active))
const passedRules = computed(() => activeRules.value.filter((entry) => entry.passed))
const errorEntries = computed(() => Object.entries(props.validation.errors))
const errorCount = computed(() => errorEntries.value.reduce((sum, [, errors]) => sum + errors.length, 0))
const progress = computed(() => visibleFields.value.length ? Math.round(visitedVisible.value.length / visibleFields.value.length * 100) : 100)
const ready = computed(() => props.validation.isValid && unvisited.value.length === 0)
</script>

<template>
  <aside class="qa-panel" aria-labelledby="qa-heading">
    <header class="qa-panel__header">
      <div>
        <span class="eyebrow">Kontrola jakości</span>
        <h2 id="qa-heading">Panel QA</h2>
      </div>
      <span class="qa-status" :class="ready ? 'qa-status--ready' : 'qa-status--open'">
        <CheckCircle2 v-if="ready" :size="16" aria-hidden="true" />
        <CircleDashed v-else :size="16" aria-hidden="true" />
        {{ ready ? 'Scenariusz zaliczony' : 'Test w toku' }}
      </span>
    </header>

    <section class="qa-progress" aria-labelledby="progress-heading">
      <div class="qa-progress__label">
        <strong id="progress-heading">Odwiedzone pola</strong>
        <span>{{ visitedVisible.length }} / {{ visibleFields.length }}</span>
      </div>
      <div class="progress-track" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100" :aria-label="`Postęp testu ${progress}%`">
        <span :style="{ width: `${progress}%` }" />
      </div>
      <p v-if="unvisited.length" class="qa-muted">Do sprawdzenia: {{ unvisited.slice(0, 3).map((entry) => entry.field.label).join(', ') }}<template v-if="unvisited.length > 3"> i {{ unvisited.length - 3 }} więcej</template>.</p>
      <p v-else class="qa-success"><CheckCircle2 :size="15" aria-hidden="true" /> Wszystkie widoczne pola zostały odwiedzone.</p>
    </section>

    <div class="qa-metrics">
      <div>
        <strong :class="errorCount ? 'text-danger' : 'text-success'">{{ errorCount }}</strong>
        <span>błędów</span>
      </div>
      <div>
        <strong>{{ activeRules.length }}</strong>
        <span>aktywnych reguł</span>
      </div>
      <div>
        <strong>{{ passedRules.length }}/{{ activeRules.length }}</strong>
        <span>reguł zaliczonych</span>
      </div>
    </div>

    <details class="qa-details" :open="errorEntries.length > 0">
      <summary>
        <span><TriangleAlert :size="17" aria-hidden="true" /> Błędy walidacji</span>
        <b>{{ errorCount }}</b>
      </summary>
      <div v-if="!errorEntries.length" class="qa-empty"><CheckCircle2 :size="17" aria-hidden="true" /> Brak błędów dla bieżącego scenariusza.</div>
      <ul v-else class="qa-list">
        <li v-for="([path, errors]) in errorEntries" :key="path">
          <code>{{ path }}</code>
          <span v-for="error in errors" :key="error">{{ error }}</span>
        </li>
      </ul>
    </details>

    <details class="qa-details" :open="validation.rules.length > 0">
      <summary>
        <span><FileCheck2 :size="17" aria-hidden="true" /> Reguły cross-field</span>
        <b>{{ passedRules.length }}/{{ activeRules.length }}</b>
      </summary>
      <div v-if="!validation.rules.length" class="qa-empty">Definicja nie zawiera reguł cross-field.</div>
      <ul v-else class="rule-list">
        <li v-for="entry in validation.rules" :key="entry.rule.id" :class="{ 'rule-list__inactive': !entry.active }">
          <CheckCircle2 v-if="entry.active && entry.passed" :size="18" aria-label="zaliczona" />
          <XCircle v-else-if="entry.active" :size="18" aria-label="niezaliczona" />
          <CircleDashed v-else :size="18" aria-label="nieaktywna" />
          <div>
            <strong>{{ entry.rule.id }}</strong>
            <span>{{ entry.active ? entry.rule.message : 'Reguła nie jest aktywna dla bieżącej ścieżki.' }}</span>
          </div>
        </li>
      </ul>
    </details>

    <section class="hash-card" aria-label="Integralność definicji">
      <ShieldCheck :size="20" aria-hidden="true" />
      <div>
        <strong>{{ integrityLabel(integrity) }}</strong>
        <span>SHA-256 definicji</span>
        <code :title="hash">{{ hash || 'obliczanie…' }}</code>
      </div>
    </section>

    <div class="qa-actions">
      <button class="button button--secondary" type="button" @click="$emit('printReport')">
        <Printer :size="17" aria-hidden="true" /> Drukuj raport testu
      </button>
      <button class="button button--primary" type="button" @click="$emit('exportReport')">
        <Download :size="17" aria-hidden="true" /> Eksportuj raport QA
      </button>
    </div>

    <p class="privacy-note">
      Raport nigdy nie zawiera wartości wpisanych w pola. Odpowiedzi pozostają wyłącznie w pamięci tej karty.
    </p>
  </aside>
</template>
